import React from 'react';

/*
 * The quiet drawing behind the front door: one entity at the centre and the
 * stakeholders around it, ring by ring.
 *
 * It is doing the thing the app does. A red line leaves the centre and works
 * its way out through the ties to somebody on the edge, holds a moment when it
 * gets there, fades, and starts again towards someone else — the shortest way
 * in, which is what the profile network computes for real.
 *
 * Deliberately colourless otherwise. Red is the only colour on the screen, and
 * here it means what it means everywhere else in the app: this one.
 *
 * Everything moves on CSS over a layout computed once at module load: no
 * timers, no re-renders, nothing to clean up. One cycle drives every route,
 * each starting a slot later than the last, and each lit slightly longer than
 * its slot so one is always fading in as the last fades out. All of
 * it stops for `prefers-reduced-motion`, which keeps the first route drawn.
 */

const CENTRE = 260;
const RINGS = [82, 154, 226];

/** Hand-placed so the rings read as organic rather than as a wheel */
const ANGLES: number[][] = [
  [-94, -22, 52, 128, 206],
  [-112, -70, -40, -2, 34, 76, 124, 178, 232],
  [-120, -86, -52, -18, 16, 48, 84, 120, 158, 206, 262],
];

const SIZES = [5.5, 4.5, 3.2];

interface Point {
  x: number;
  y: number;
}

const at = (radius: number, degrees: number): Point => ({
  x: CENTRE + radius * Math.cos((degrees * Math.PI) / 180),
  y: CENTRE + radius * Math.sin((degrees * Math.PI) / 180),
});

const gap = (a: number, b: number): number => {
  const turn = (value: number) => ((value % 360) + 360) % 360;
  const difference = Math.abs(turn(a) - turn(b));
  return Math.min(difference, 360 - difference);
};

/** Every node hangs off the one nearest to it on the ring inside */
const nearest = (degrees: number, choices: number[]): number =>
  choices.reduce((best, choice) => (gap(degrees, choice) < gap(degrees, best) ? choice : best), choices[0]);

/** Spread a value across a range from an index, so timings vary but never change */
const varied = (seed: number, min: number, max: number): number =>
  Number((min + (((seed * 37) % 23) / 23) * (max - min)).toFixed(2));

interface Node extends Point {
  r: number;
}

interface Link {
  from: Point;
  to: Point;
}

const nodes: Node[] = [];
const links: Link[] = [];

ANGLES.forEach((ring, depth) => {
  ring.forEach((degrees) => {
    const point = at(RINGS[depth], degrees);
    nodes.push({ ...point, r: SIZES[depth] });
    const parent = depth === 0 ? { x: CENTRE, y: CENTRE } : at(RINGS[depth - 1], nearest(degrees, ANGLES[depth - 1]));
    links.push({ from: parent, to: point });
  });
});

/** A handful of ties carry a travelling signal — enough to read as live, few enough to ignore */
const SIGNALS = links.filter((_, index) => index % 4 === 1);

interface Route {
  /** Centre to target, hop by hop, along ties that are actually drawn */
  d: string;
  target: Point;
}

/** The way out to one node: up the chain of nearest parents, then reversed */
const routeTo = (depth: number, degrees: number): Route => {
  const points: Point[] = [];
  let angle = degrees;
  for (let level = depth; level >= 0; level -= 1) {
    points.unshift(at(RINGS[level], angle));
    if (level > 0) angle = nearest(angle, ANGLES[level - 1]);
  }
  points.unshift({ x: CENTRE, y: CENTRE });
  return {
    d: points.map((point, index) => `${index ? 'L' : 'M'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' '),
    target: points[points.length - 1],
  };
};

/**
 * Who the line goes to, in order, scattered rather than swept. Both lists stay
 * clear of ground the screen uses for something else: the caption sits over
 * the foot of the panel, and the ribbon is only a slice of the map.
 */
const ROUTES: Record<'panel' | 'ribbon', Route[]> = {
  panel: [routeTo(2, -52), routeTo(2, 158), routeTo(1, -112), routeTo(2, 16), routeTo(2, 206)],
  ribbon: [routeTo(2, -52), routeTo(1, -112), routeTo(2, -18), routeTo(1, -70), routeTo(2, -120)],
};

/** One slot each, and the cycle is all of them */
const SLOT = 6;
const CYCLE = SLOT * ROUTES.panel.length;

/**
 * `pathLength` normalises every tie and route to 100 units, so one dash
 * pattern and one keyframe work on any length. At the resting offset the dash
 * sits before the start, which is why nothing shows when animation is off.
 */
const DASH = '7 100';
const RESTING = 7;

const CSS = `
.sm-sway { animation: sm-sway 34s ease-in-out infinite alternate; transform-origin: ${CENTRE}px ${CENTRE}px; }
.sm-node { animation: sm-in .6s ease-out both, sm-breathe var(--sm-dur) ease-in-out infinite alternate var(--sm-delay); }
.sm-tie { animation: sm-in .7s ease-out both; animation-delay: var(--sm-delay); }
.sm-signal { animation: sm-signal var(--sm-dur) cubic-bezier(.45,0,.55,1) infinite var(--sm-delay); }
.sm-route { animation: sm-route ${CYCLE}s linear infinite both; animation-delay: var(--sm-delay); }
.sm-target { animation: sm-target ${CYCLE}s linear infinite both; animation-delay: var(--sm-delay); }
.sm-find { animation: sm-find ${CYCLE}s linear infinite both; animation-delay: var(--sm-delay); transform-origin: var(--sm-origin); }
@keyframes sm-sway { from { transform: rotate(-1.6deg); } to { transform: rotate(1.6deg); } }
@keyframes sm-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes sm-breathe { from { opacity: .5; } to { opacity: 1; } }
@keyframes sm-signal { from { stroke-dashoffset: ${RESTING}; } to { stroke-dashoffset: -100; } }
@keyframes sm-route {
  0% { stroke-dashoffset: 100; opacity: 1; }
  9% { stroke-dashoffset: 0; opacity: 1; }
  18% { stroke-dashoffset: 0; opacity: 1; }
  22%, 100% { stroke-dashoffset: 0; opacity: 0; }
}
@keyframes sm-target {
  0%, 7% { opacity: 0; }
  10%, 18% { opacity: 1; }
  22%, 100% { opacity: 0; }
}
@keyframes sm-find {
  0%, 9% { transform: scale(.7); opacity: 0; }
  11% { opacity: .45; }
  21%, 100% { transform: scale(2); opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .sm-sway, .sm-node, .sm-tie, .sm-signal, .sm-route, .sm-target, .sm-find { animation: none; }
  .sm-first { stroke-dashoffset: 0; opacity: 1; }
}
`;

/** `panel` shows the whole map; `ribbon` is a wide slice for the top of a phone screen */
const VIEW = {
  panel: '0 0 520 520',
  ribbon: '120 52 472 180',
};

type Style = React.CSSProperties & Record<`--sm-${string}`, string>;

export const StakeholderMap: React.FC<{ variant?: keyof typeof VIEW; className?: string }> = ({
  variant = 'panel',
  className = '',
}) => (
  <svg
    viewBox={VIEW[variant]}
    preserveAspectRatio={variant === 'ribbon' ? 'xMidYMid slice' : 'xMidYMid meet'}
    aria-hidden="true"
    focusable="false"
    className={className}
    fill="none"
  >
    <style>{CSS}</style>
    <g className="sm-sway">
      <g stroke="#e5e5e5">
        {RINGS.map((radius) => (
          <circle key={radius} cx={CENTRE} cy={CENTRE} r={radius} />
        ))}
      </g>

      {/* the ties, still */}
      <g stroke="#e5e5e5">
        {links.map((link, index) => (
          <line
            key={index}
            className="sm-tie"
            style={{ '--sm-delay': `${varied(index, 0, 0.5)}s` } as Style}
            x1={link.from.x}
            y1={link.from.y}
            x2={link.to.x}
            y2={link.to.y}
          />
        ))}
      </g>

      {/* something travelling a few of them */}
      <g stroke="#a3a3a3" strokeWidth={1.75} strokeLinecap="round">
        {SIGNALS.map((link, index) => (
          <line
            key={index}
            className="sm-signal"
            style={
              {
                '--sm-dur': `${varied(index + 3, 6, 11)}s`,
                '--sm-delay': `${varied(index + 7, 0.8, 9)}s`,
              } as Style
            }
            x1={link.from.x}
            y1={link.from.y}
            x2={link.to.x}
            y2={link.to.y}
            pathLength={100}
            strokeDasharray={DASH}
            strokeDashoffset={RESTING}
          />
        ))}
      </g>

      <g>
        {nodes.map((node, index) => (
          <circle
            key={index}
            className="sm-node"
            style={
              {
                '--sm-dur': `${varied(index, 5, 9)}s`,
                '--sm-delay': `${varied(index + 2, 0.6, 4)}s`,
              } as Style
            }
            cx={node.x}
            cy={node.y}
            r={node.r}
            fill="#d4d4d4"
          />
        ))}
      </g>

      {/* the line working its way out to one person, then to the next */}
      {ROUTES[variant].map((route, index) => {
        const delay = { '--sm-delay': `${index * SLOT}s` } as Style;
        const first = index === 0 ? ' sm-first' : '';
        return (
          <g key={route.d}>
            <path
              className={`sm-route${first}`}
              style={delay}
              d={route.d}
              pathLength={100}
              strokeDasharray="100 100"
              strokeDashoffset={100}
              stroke="#ea2a33"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={0}
            />
            <circle
              className="sm-find"
              style={{ ...delay, '--sm-origin': `${route.target.x}px ${route.target.y}px` } as Style}
              cx={route.target.x}
              cy={route.target.y}
              r={13}
              stroke="#ea2a33"
              opacity={0}
            />
            <g className={`sm-target${first}`} style={delay} opacity={0}>
              <circle cx={route.target.x} cy={route.target.y} r={13} stroke="rgba(234,42,51,0.3)" />
              <circle cx={route.target.x} cy={route.target.y} r={6} fill="#ea2a33" />
            </g>
          </g>
        );
      })}

      <circle cx={CENTRE} cy={CENTRE} r={9} fill="#0a0a0a" />
    </g>
  </svg>
);
